import React, { Component } from 'react';
import { observer } from 'mobx-react';

import { Col, Grid, Row, Table } from 'react-bootstrap';

import { FormattingUtils } from '../../../utils';
import { ILoan } from '../../../interfaces';

import PaymentAllocationRow from './PaymentAllocationRow';
import { IAllocationDraft, draftTotal } from './allocations';

const { formatMoney } = FormattingUtils;

// The totals rows label themselves across everything to the left of the amount column.
const LABEL_COLUMNS = 3;

interface IProps {
  allocations: IAllocationDraft[];
  // The whole payment, to reconcile the rows against. A string because it comes straight off the
  // money input beside this table.
  amount: string;
  // The loans this payment closes, by id -- see LogLoanPaymentModal.closingLoanIds
  closingLoanIds: string[];
  loansById: { [id: string]: ILoan };
  onChange: (loanId: string, field: 'amount_to_interest' | 'amount_to_principal', value: string) => void;
  onCloseChange: (loanId: string, closesLoan: boolean) => void;
}

/**
 * The proposed split, laid out one row per loan and editable.
 *
 * The API works the split out (oldest loan first, interest before principal) and this is where the
 * user confirms or overrides it, which is the arrangement M-1007 asks for: "this could be done in
 * the UI with the user confirming how the payment is applied". Nothing is written until the modal
 * around this posts the rows.
 *
 * The reconciliation line at the foot is the point of the table. A payment's allocations have to sum
 * to it exactly or the API refuses the lot, so the difference is shown as it is edited rather than
 * left to come back as a validation error on save.
 */
@observer
class PaymentAllocationsTable extends Component<IProps> {
  /**
   * Allocated against the payment, and what is left between them.
   *
   * A shortfall is the ordinary state while the user is still typing, so it is not an error until
   * they try to save -- the modal disables the button rather than this table shouting.
   */
  private renderTotals () {
    const { allocations, amount } = this.props
      , allocated = draftTotal(allocations)
      , difference = Number(amount || 0) - allocated
      ;

    return (
      <tfoot>
        {/* The trailing cell keeps each figure under the Total column rather than under the Close
            checkbox beside it. */}
        <tr className='allocations-total'>
          <th colSpan={LABEL_COLUMNS}>Allocated</th>
          <th className='text-right'>{formatMoney(allocated)}</th>
          <th />
        </tr>
        {!!difference && (
          <tr className='allocations-unallocated text-danger'>
            <th colSpan={LABEL_COLUMNS}>{difference > 0 ? 'Unallocated' : 'Over-allocated'}</th>
            <th className='text-right'>{formatMoney(Math.abs(difference))}</th>
            <th />
          </tr>
        )}
      </tfoot>
    );
  }

  private renderTable () {
    const { allocations, closingLoanIds, loansById, onChange, onCloseChange } = this.props;

    if (!allocations.length) {
      return (
        <div className='empty'>
          Enter an amount and a payment date to see how it would be applied.
        </div>
      );
    }

    return (
      <Table className='payment-allocations fix-layout' condensed>
        <thead>
          <tr>
            <th className='col-sm-3'>Loan</th>
            <th className='col-sm-3'>Interest</th>
            <th className='col-sm-3'>Principal</th>
            <th className='col-sm-2 text-right'>Total</th>
            <th className='col-sm-1 text-center' title='Closes the loan'>Close</th>
          </tr>
        </thead>
        <tbody>
          {allocations.map(allocation => (
            <PaymentAllocationRow
              allocation={allocation}
              closesLoan={closingLoanIds.indexOf(allocation.loan) !== -1}
              key={allocation.loan}
              loan={loansById[allocation.loan]}
              onChange={onChange}
              onCloseChange={onCloseChange}
            />
          ))}
        </tbody>
        {this.renderTotals()}
      </Table>
    );
  }

  public render () {
    return (
      <Grid className='table-allocations' fluid>
        <Row>
          <Col xs={12}><h3>Payment Allocation</h3></Col>
        </Row>
        <Row>
          <Col xs={12}>{this.renderTable()}</Col>
        </Row>
      </Grid>
    );
  }
}

export default PaymentAllocationsTable;
